import type { Entity, LibraryGroup, NodeType, StoryNode, Connection } from '@dispositio/core';
import type { ChangeSet, StoryPayload } from '@dispositio/shared';

function merge<T extends { id: string }>(list: T[], changed: T[], removed: string[]): T[] {
  if (changed.length === 0 && removed.length === 0) return list;
  const gone = new Set(removed);
  const byId = new Map(changed.map((c) => [c.id, c]));
  const seen = new Set<string>();
  const out: T[] = [];
  for (const item of list) {
    if (gone.has(item.id)) continue;
    const next = byId.get(item.id);
    out.push(next ?? item);
    if (next) seen.add(item.id);
  }
  for (const c of changed) if (!seen.has(c.id) && !gone.has(c.id)) out.push(c);
  return out;
}

/** Wendet die Antwort einer Änderung auf die lokal gehaltene Geschichte an (ersetzt gleiche IDs, entfernt gelöschte). */
export function applyChanges(story: StoryPayload, cs: ChangeSet): StoryPayload {
  const removedNodes = new Set(cs.removed.nodes);
  // Verbindungen verschwinden auch, wenn einer ihrer Enden gelöscht wurde (der Server meldet sie ebenfalls, das hier ist die Absicherung)
  const connections = merge<Connection>(story.connections, cs.connections, cs.removed.connections).filter(
    (c) => !removedNodes.has(c.sourceId) && !removedNodes.has(c.targetId),
  );
  return {
    ...story,
    nodes: merge<StoryNode>(story.nodes, cs.nodes, cs.removed.nodes),
    entities: merge<Entity>(story.entities, cs.entities, cs.removed.entities),
    groups: merge<LibraryGroup>(story.groups, cs.groups, cs.removed.groups),
    connections,
  };
}

export function applyTypeChange(story: StoryPayload, type: NodeType | null, removedId?: string): StoryPayload {
  return { ...story, types: merge<NodeType>(story.types, type ? [type] : [], removedId ? [removedId] : []).sort((a, b) => a.sort - b.sort) };
}

/** Sofort sichtbarer Teil einer Node-Änderung (vor der Serverantwort). */
export function optimisticNode(node: StoryNode, patch: Partial<Pick<StoryNode, 'parentId' | 'x' | 'y' | 'at' | 'from' | 'to' | 'tagColor' | 'dynamicFields'>> & { fields?: StoryNode['fields'] }): StoryNode {
  return { ...node, ...patch, fields: patch.fields ? { ...node.fields, ...patch.fields } : node.fields };
}
